import {
    LocalStorageServiceInterface,
    LocalStorageServiceOptions,
    StorageValidationSchema,
} from './LocalStorageService.types';

/**
 * Сервис для работы с localStorage с типизацией и валидацией данных
 */
export class LocalStorageService implements LocalStorageServiceInterface {
    private readonly keyPrefix: string;
    private readonly enableLogging: boolean;

    constructor(options: LocalStorageServiceOptions = {}) {
        this.keyPrefix = options.keyPrefix || '';
        this.enableLogging = options.enableLogging ?? true;
    }

    /**
     * Проверяет доступность localStorage
     */
    public isAvailable(): boolean {
        try {
            const testKey = '__localStorage_test__';
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Загружает данные из localStorage с валидацией по схеме
     */
    public load<T>(key: string, schema: StorageValidationSchema<T>): T {
        if (!this.isAvailable()) {
            return this.createDefaultObject(schema);
        }

        try {
            const fullKey = this.getFullKey(key);
            const stored = localStorage.getItem(fullKey);

            if (!stored) {
                return this.createDefaultObject(schema);
            }

            const parsed = JSON.parse(stored) as Partial<T>;

            return this.validateAndMerge(parsed, schema);
        } catch (error) {
            this.logError(`Ошибка при загрузке данных из localStorage для ключа "${key}":`, error);

            return this.createDefaultObject(schema);
        }
    }

    /**
     * Сохраняет данные в localStorage
     */
    public save<T>(key: string, data: T): void {
        if (!this.isAvailable()) {
            return;
        }

        try {
            const fullKey = this.getFullKey(key);
            localStorage.setItem(fullKey, JSON.stringify(data));
        } catch (error) {
            this.logError(`Ошибка при сохранении данных в localStorage для ключа "${key}":`, error);
        }
    }

    /**
     * Удаляет данные из localStorage
     */
    public remove(key: string): void {
        if (!this.isAvailable()) {
            return;
        }

        try {
            const fullKey = this.getFullKey(key);
            localStorage.removeItem(fullKey);
        } catch (error) {
            this.logError(`Ошибка при удалении данных из localStorage для ключа "${key}":`, error);
        }
    }

    /**
     * Очищает весь localStorage
     */
    public clear(): void {
        if (!this.isAvailable()) {
            return;
        }

        try {
            localStorage.clear();
        } catch (error) {
            this.logError('Ошибка при очистке localStorage:', error);
        }
    }

    /**
     * Проверяет существование ключа в localStorage
     */
    public exists(key: string): boolean {
        if (!this.isAvailable()) {
            return false;
        }

        try {
            const fullKey = this.getFullKey(key);

            return localStorage.getItem(fullKey) !== null;
        } catch {
            return false;
        }
    }

    /**
     * Получает полный ключ с префиксом
     */
    private getFullKey(key: string): string {
        return this.keyPrefix ? `${this.keyPrefix}_${key}` : key;
    }

    /**
     * Создает объект со значениями по умолчанию на основе схемы
     */
    private createDefaultObject<T>(schema: StorageValidationSchema<T>): T {
        const result = {} as T;

        for (const [key, config] of Object.entries(schema) as Array<[keyof T, StorageValidationSchema<T>[keyof T]]>) {
            result[key] = config.defaultValue;
        }

        return result;
    }

    /**
     * Валидирует загруженные данные по схеме и объединяет с значениями по умолчанию
     */
    private validateAndMerge<T>(data: Partial<T>, schema: StorageValidationSchema<T>): T {
        const result = {} as T;

        for (const [key, config] of Object.entries(schema) as Array<[keyof T, StorageValidationSchema<T>[keyof T]]>) {
            const value = data[key];

            if (this.isValidValue(value, config.type)) {
                result[key] = value as T[keyof T];
            } else {
                result[key] = config.defaultValue;
            }
        }

        return result;
    }

    /**
     * Проверяет соответствие значения ожидаемому типу
     */
    private isValidValue(value: unknown, expectedType: string): boolean {
        switch (expectedType) {
            case 'string':
                return typeof value === 'string';
            case 'number':
                return typeof value === 'number';
            case 'boolean':
                return typeof value === 'boolean';
            case 'array':
                return Array.isArray(value);
            case 'object':
                return typeof value === 'object' && value !== null && !Array.isArray(value);
            default:
                return false;
        }
    }

    /**
     * Логирует ошибку, если включено логирование
     */
    private logError(message: string, error: unknown): void {
        if (this.enableLogging) {
            console.warn(message, error);
        }
    }
}

/**
 * Экземпляр сервиса по умолчанию
 */
export const localStorageService = new LocalStorageService();
